import { getToken, clearToken } from "./auth";

const BASE = import.meta.env.VITE_API_BASE ?? "";

export type PromptTestState = "pending" | "running" | "succeeded" | "failed";

/**
 * Ein Prompt-Testlauf: Character-Sheet + eine Szenen-Platzierung für einen
 * Upload, mit exakt den Prompts einer echten Bestellung, nur in kleiner
 * Auflösung. Siehe src/services/prompt-test.ts.
 */
export interface PromptTestRun {
  id: string;
  uniqid: string;
  bookId: string;
  sceneIndex: number;
  state: PromptTestState;
  sceneWidth: number;
  sceneHeight: number;
  reuseSheet: boolean;
  sheetReused: boolean;
  childName: string;
  gender: "m" | "f" | "d";
  hairColor: string | null;
  eyeColor: string | null;
  skinTone: string | null;
  sheetPrompt: string | null;
  scenePrompt: string | null;
  outfitDescription: string | null;
  charIdentity: string | null;
  sceneSettings: Record<string, unknown> | null;
  sheetPath: string | null;
  scenePath: string | null;
  sheetMs: number | null;
  sceneMs: number | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface PromptTestSceneOption {
  index: number;
  name: string;
  enabled: boolean;
  emotion: string | null;
  provider: "openai" | "gemini";
  /** Auflösung, die eine echte Bestellung rendern würde. */
  order_size: string;
  /** Vorgeschlagene Testauflösung: beste 16er-Näherung an dieses Verhältnis. */
  suggested_width: number;
  suggested_height: number;
}

export interface PromptTestBookOption {
  id: string;
  slug: string;
  title: string;
  scenes: PromptTestSceneOption[];
}

export interface PromptTestOptions {
  target_long_edge_px: number;
  books: PromptTestBookOption[];
}

export interface CreatePromptTestInput {
  uniqid: string;
  bookId?: string;
  sceneIndex?: number;
  sceneWidth?: number;
  sceneHeight?: number;
  reuseSheet?: boolean;
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = getToken();
  const res = await fetch(`${BASE}/api/prompt-tests${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  return res;
}

/** Server-Fehlermeldung durchreichen — sie erklärt, warum ein Lauf abgelehnt wurde. */
async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body && typeof body.error === "string") message = body.error;
    } catch {
      /* non-JSON body — fall back to the status code */
    }
    throw new Error(message);
  }
  return res.json();
}

export const promptTestsApi = {
  options: () => authedFetch("/options").then((r) => unwrap<PromptTestOptions>(r)),

  list: (uniqid: string, limit = 10) =>
    authedFetch(`/?uniqid=${encodeURIComponent(uniqid)}&limit=${limit}`).then((r) =>
      unwrap<{ uniqid: string; runs: PromptTestRun[] }>(r),
    ),

  get: (id: string) => authedFetch(`/${id}`).then((r) => unwrap<PromptTestRun>(r)),

  create: (input: CreatePromptTestInput) =>
    authedFetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }).then((r) => unwrap<PromptTestRun>(r)),

  remove: (id: string) =>
    authedFetch(`/${id}`, { method: "DELETE" }).then((r) =>
      unwrap<{ id: string; deleted: boolean }>(r),
    ),
};

/** URL des Ergebnisbildes — der Abruf selbst läuft über AuthAsset (Token-Header). */
export function promptTestImageUrl(id: string, kind: "sheet" | "scene"): string {
  return `${BASE}/api/prompt-tests/${id}/image/${kind}`;
}
